/* eslint-disable max-lines */
import { useState } from 'react'
import { Box, Button, Collapse, Stack, Typography } from '@mui/material'
import { ChevronDown20Regular, ChevronRight20Regular, Warning24Regular } from '@/components/Icon'
import Pressable from '@/theme/Pressable'
import { useProposalDecision } from '@/features/chat/phase1/useProposalDecision'
import ProposalOutcome from './ProposalOutcome'

export default function ConfirmationCardBlock({ block }) {
  const [showArgs, setShowArgs] = useState(false)
  const { settled, label, tone, error, pending, checking, decide } = useProposalDecision(
    block.proposal_id,
    block.decision,
  )

  const argsText =
    typeof block.arguments_preview === 'string'
      ? block.arguments_preview
      : JSON.stringify(block.arguments_preview ?? {}, null, 2)

  // A delete and a draft reach this card by the same route, so the card is the
  // only thing that can tell them apart before the click.
  const danger = Boolean(block.destructive)

  return (
    <Box
      sx={{
        p: 1.75,
        borderRadius: 'var(--app-radius-md)',
        border: 'none',
        bgcolor: 'var(--app-bg-hover)',
        maxWidth: 500,
      }}
    >
      <Stack direction="row" sx={{ gap: 1, alignItems: 'flex-start' }}>
        <Box
          sx={{
            color: danger ? 'var(--app-error, #a4262c)' : 'var(--app-warning, #d83b01)',
            mt: '1px',
            display: 'flex',
          }}
        >
          <Warning24Regular fontSize={20} />
        </Box>
        <Stack sx={{ flex: 1, minWidth: 0, gap: 0.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 650 }}>
            {danger ? 'Confirm — this cannot be undone' : 'Confirm action'}
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'var(--app-text-secondary)' }}>
            {block.summary || `Run ${block.tool}`}
          </Typography>
          {block.description ? (
            <Typography sx={{ fontSize: 13, color: 'var(--app-text-secondary)' }}>
              {block.description}
            </Typography>
          ) : null}

          {block.arguments_preview !== undefined ? (
            <>
              <Pressable
                aria-expanded={showArgs}
                onClick={() => setShowArgs((value) => !value)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 0.5,
                  fontSize: 12,
                  color: 'var(--app-text-secondary)',
                }}
              >
                {showArgs ? <ChevronDown20Regular fontSize={14} /> : <ChevronRight20Regular fontSize={14} />}
                Details
              </Pressable>
              <Collapse in={showArgs}>
                <Box
                  component="pre"
                  sx={{
                    mt: 0.5,
                    p: 1,
                    fontSize: 12,
                    fontFamily: 'var(--app-font-mono, ui-monospace, monospace)',
                    bgcolor: 'var(--app-bg-hover)',
                    borderRadius: 'var(--app-radius-xs)',
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {argsText}
                </Box>
              </Collapse>
            </>
          ) : null}

          {settled ? (
            <ProposalOutcome label={label} tone={tone} error={error} />
          ) : (
            <Stack direction="row" sx={{ gap: 1, mt: 1, alignItems: 'center' }}>
              <Button
                size="small"
                variant="contained"
                color={danger ? 'error' : 'primary'}
                disabled={pending || checking}
                onClick={() => decide(true)}
              >
                {checking ? 'Checking…' : pending ? 'Working…' : danger ? 'Delete anyway' : 'Approve'}
              </Button>
              <Button size="small" variant="text" disabled={pending || checking} onClick={() => decide(false)}>
                Cancel
              </Button>
              {error ? (
                <Typography sx={{ fontSize: 12, color: 'var(--app-error, #a4262c)' }}>
                  {error}
                </Typography>
              ) : null}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Box>
  )
}
